import { useEffect, useMemo } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import { shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import {
    Card,
    ChangeValue,
    Tooltip,
    TitlePlaceholderLoader,
    ListItemPlaceholderLoader,
} from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { toggleAlternativeSource } from "app/actions/commonActions";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import type { ICommonWidgetProps, IWidgetPOPParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import DurationService from "app/services/DurationService";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";

import { useTotalVisitsData } from "./useTotalVisitsData";
import { allTrackers } from "app/services/track/track";

const POPBar = styled.div<{ barHeight?: string; color: string }>`
    width: 24px;
    height: ${({ barHeight }) => barHeight};
    background-color: ${({ color }) => color};
    border-radius: 2px 2px 0 0;
`;

const TOTAL_VISITS_DATA_KEY = "TotalVisits";

const PERIOD_COLORS = {
    current: colorsPalettes.blue[400],
    compared: colorsPalettes.orange[400],
} as const;

const calculateBarHeights = (currentValue: number, comparedValue: number) => {
    const isCurrentHighest = currentValue > comparedValue;
    return {
        current: isCurrentHighest ? "100%" : `${(currentValue / comparedValue) * 100}%`,
        compared: isCurrentHighest ? `${(comparedValue / currentValue) * 100}%` : "100%",
    };
};

interface ITotalVisitsPOPWidgetProps extends ICommonWidgetProps {
    params: IWidgetPOPParams;
}

export const TotalVisitsPOPWidget = ({
    params,
    dateRangeText,
    countryText,
    countryCode,
    showGAApprovedData,
    isGAPrivate,
}: ITotalVisitsPOPWidgetProps) => {
    const { translate } = useTranslate();
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger("total-visits-pop-widget");
    const { webSource, duration, comparedDuration, key } = params;

    const { data, isLoading, isError } = useTotalVisitsData(
        {
            country: params.country.toString(),
            from: params.from,
            to: params.to,
            keys: params.key,
            webSource: params.webSource,
            isWindow: params.isWindow,
            includeSubDomains: !params.isWWW,
            timeGranularity: "Monthly",
            compareFrom: params.compareFrom,
            compareTo: params.compareTo,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ShouldGetVerifiedData: showGAApprovedData,
        },
        "TrafficAndEngagement",
    );

    const durationObject = DurationService.getDurationData(duration, comparedDuration);

    const dataObj = useMemo(() => {
        const dataArray = data?.Data?.[key];
        if (!Array.isArray(dataArray) || dataArray.length < 2) {
            return {};
        }

        const currentValue = dataArray[0]?.[TOTAL_VISITS_DATA_KEY];
        const comparedValue = dataArray[1]?.[TOTAL_VISITS_DATA_KEY];

        if (currentValue != null && comparedValue != null) {
            const barHeights = calculateBarHeights(currentValue, comparedValue);
            return {
                currentPeriod: { value: currentValue, barHeight: barHeights.current },
                comparedPeriod: { value: comparedValue, barHeight: barHeights.compared },
            };
        }
        return {};
    }, [data?.Data, key]);

    const { comparedPeriod, currentPeriod } = dataObj;
    const changeValue = Array.isArray(data?.Data?.[key]) && data.Data[key][0] ? data.Data[key][0].Change : undefined;

    const dispatch = useDispatch();

    useEffect(() => {
        if (!data) {
            return;
        }

        const fromAlternativeSources = !!data?.FromAlternativeSources;
        dispatch(toggleAlternativeSource(fromAlternativeSources));
        if (data.FromAlternativeSources === true) {
            allTrackers.dispatchTrackEvent(
                "Internal Link",
                "show",
                `WebAnalysis/TrafficAndEngagement/AlternativeSource/${params.key || ""}`,
            );
        }

        return () => {
            dispatch(toggleAlternativeSource(false));
        };
    }, [data?.FromAlternativeSources, dispatch, params.key]);

    const isEmpty = !currentPeriod?.value && !comparedPeriod?.value;

    if (isLoading) {
        return (
            <Card data-automation="total-visits-pop-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                >
                    <TitlePlaceholderLoader />
                    <ListItemPlaceholderLoader />
                    <ListItemPlaceholderLoader />
                </Flex>
            </Card>
        );
    }

    return (
        <Card data-automation="total-visits-pop-widget">
            {isError || isEmpty ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    flex="1"
                    height="100%"
                >
                    <NoDataLandscape
                        title="global.nodata.notavilable"
                        subtitle={null}
                    />
                </Flex>
            ) : (
                <>
                    <Card.Header withDivider>
                        <Card.Title>
                            <Flex
                                gap="8px"
                                alignItems="center"
                            >
                                <Text
                                    fontVariant="h5_2"
                                    data-automation="total-visits-pop-widget-title"
                                >
                                    {translate("wa.ao.totalvisits")}
                                </Text>
                                <Flex alignItems="center">
                                    {showGAApprovedData ? (
                                        <span data-automation="total-visits-pop-widget-title-ga-verified">
                                            <GAVerifiedContainer
                                                size="SMALL"
                                                isActive={true}
                                                isPrivate={isGAPrivate}
                                                tooltipAvailable={true}
                                                metric="EngagementVisits"
                                                tooltipIsActive={false}
                                                top={4}
                                            />
                                        </span>
                                    ) : null}
                                    <span data-automation="total-visits-pop-widget-title-tooltip">
                                        <Tooltip content={translate("wa.ao.totalvisits.tooltip")}>
                                            <Info24px
                                                size={24}
                                                color={colorsPalettes.carbon[200]}
                                                data-automation="total-visits-pop-widget-title-info-icon"
                                            />
                                        </Tooltip>
                                    </span>
                                </Flex>
                            </Flex>
                            <Card.Query>
                                <Card.QueryItem
                                    iconName="daily-ranking"
                                    data-automation="total-visits-pop-widget-date-range"
                                    {...durationTriggerProps}
                                >
                                    {dateRangeText}
                                </Card.QueryItem>
                                <Flex
                                    gap="8px"
                                    alignItems="center"
                                    data-automation="total-visits-pop-widget-country"
                                    {...countryTriggerProps}
                                >
                                    <SWReactCountryIcons
                                        countryCode={countryCode}
                                        size="xs"
                                    />
                                    <Text
                                        fontVariant="chip1"
                                        color={colorsPalettes.carbon[400]}
                                    >
                                        {countryText}
                                    </Text>
                                </Flex>
                            </Card.Query>
                        </Card.Title>
                        <AddToDashboard
                            modelType="fromWebsite"
                            metric="EngagementVisits"
                            type="PeriodOverPeriodVisits"
                            webSource={webSource as IWidgetModelTypesWebSource}
                            filters={{
                                ShouldGetVerifiedData: showGAApprovedData,
                            }}
                        />
                    </Card.Header>
                    <Flex
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        padding="24px"
                        gap="16px"
                        flex="1"
                    >
                        {changeValue != null ? (
                            <ChangeValue
                                value={percentageSignFilter()(changeValue, 2)}
                                isDecrease={changeValue < 0}
                                unsigned={changeValue === 0}
                            />
                        ) : null}
                        <Flex
                            justifyContent="center"
                            alignItems="flex-end"
                            gap="24px"
                            width="100%"
                            maxWidth="400px"
                        >
                            <Flex
                                flex="1"
                                flexDirection="column"
                                alignItems="flex-end"
                                gap="4px"
                            >
                                <Text
                                    fontVariant="h3"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {minVisitsAbbrFilter()(comparedPeriod?.value)}
                                </Text>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[250]}
                                    textAlign="center"
                                >
                                    {`${durationObject.raw.compareFrom.format(shortMonthAndShortYear)} - ${durationObject.raw.compareTo.format(shortMonthAndShortYear)}`}
                                </Text>
                            </Flex>
                            <Flex
                                gap="8px"
                                alignItems="flex-end"
                                height="64px"
                            >
                                <POPBar
                                    color={PERIOD_COLORS.compared}
                                    barHeight={comparedPeriod?.barHeight}
                                />
                                <POPBar
                                    color={PERIOD_COLORS.current}
                                    barHeight={currentPeriod?.barHeight}
                                />
                            </Flex>
                            <Flex
                                flex="1"
                                flexDirection="column"
                                alignItems="flex-start"
                                gap="4px"
                            >
                                <Text
                                    fontVariant="h3"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {minVisitsAbbrFilter()(currentPeriod?.value)}
                                </Text>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[250]}
                                    textAlign="center"
                                >
                                    {`${durationObject.raw.from.format(shortMonthAndShortYear)} - ${durationObject.raw.to.format(shortMonthAndShortYear)}`}
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </>
            )}
        </Card>
    );
};
